/**
 * Generate Jig AI-prompt module.
 * Builds the prompt payloads sent to Spark, parses AI responses back into stage
 * instructions, and provides fallback stage content when generation fails.
 */
import { AppConst } from '@/constants/app'
import type { AxiosRequestConfig } from 'axios'
import { postToSpark, postToSparkWithFallback } from './generate-jig.auth'
import type { StageInstructionWithTools, UseCasePromptFlavoring, ViewpointToolData } from './generate-jig.types'
import { deepClone, generateShortUid, isRequestCanceled } from './generate-jig.utils'

declare var consoleError: Function
declare var consoleWarn: Function

const USER_PROMPT_SPLIT_MARKER = '=== AVAILABLE VISUAL TOOLS ==='
const DEFAULT_SPARK_AI_PROVIDER = 'openai'
const DEFAULT_SPARK_AI_MODEL = 'gpt-5.4-mini'
const DEFAULT_SPARK_AI_MAX_TOKENS = 15000
const DEFAULT_SPARK_AI_TEMPERATURE = 0.7

export function getSparkAiProvider(): string {
  const provider = process.env.VUE_APP_AI_PROVIDER || process.env.VUE_APP_WEBSPARK_AI_PROVIDER || ''

  if (provider && provider.trim().length > 0) {
    return provider.trim()
  }

  return DEFAULT_SPARK_AI_PROVIDER
}

export function getSparkAiModel(): string {
  const model = process.env.VUE_APP_AI_MODEL || process.env.VUE_APP_WEBSPARK_AI_MODEL || ''

  if (model && model.trim().length > 0) {
    return model.trim()
  }

  return DEFAULT_SPARK_AI_MODEL
}

export function getSparkAiMaxTokens(): number {
  const rawValue = process.env.VUE_APP_AI_MAX_TOKENS || process.env.VUE_APP_WEBSPARK_AI_MAX_TOKENS || ''
  const parsedValue = parseInt(rawValue, 10)

  if (!isNaN(parsedValue) && parsedValue > 0) {
    return parsedValue
  }

  return DEFAULT_SPARK_AI_MAX_TOKENS
}

export function getSparkAiTemperature(provider: string): number | undefined {
  if (provider === 'cerebras') {
    return undefined
  }

  return DEFAULT_SPARK_AI_TEMPERATURE
}

export function buildFallbackStageInstructions(userQuestion: string): StageInstructionWithTools[] {
  const cleanQuestion = userQuestion && userQuestion.trim().length > 0 ? userQuestion.trim() : 'this model'

  return [
    {
      instruction: 'This stage introduces ' + cleanQuestion + ' and outlines the key components visible in the model.',
    },
    {
      instruction: 'This stage explains the most important parts and how they connect to each other in the assembly.',
    },
    {
      instruction: 'This stage summarizes the function of the model and the main points to remember.',
    },
  ]
}

export function buildDynamicEnhancementSection(userQuestion: string, modelFileName: string, serializedElements: any[]): string {
  const componentList = serializedElements
    .map(function (item: any) {
      const objectName = item && item.ElementData && item.ElementData.ObjectName ? String(item.ElementData.ObjectName) : ''
      if (!objectName) {
        return ''
      }

      const afterTripleUnderscore = objectName.indexOf('___') >= 0 ? objectName.split('___').pop() : objectName
      const readableName = afterTripleUnderscore && afterTripleUnderscore.indexOf('_') >= 0
        ? String(afterTripleUnderscore.split('_').pop())
        : String(afterTripleUnderscore || objectName)

      return '"' + objectName + '" (' + readableName + ')'
    })
    .filter(function (value: string) {
      return value.length > 0
    })
    .join(', ')

  const safeQuestion = userQuestion && userQuestion.trim().length > 0 ? userQuestion.trim() : 'How does this work?'

  return [
    'User Question: ' + safeQuestion,
    'Model File: ' + modelFileName,
    'Available Components: ' + componentList,
  ].join('\n')
}

export function buildToolsPromptSection(): string {
  return [
    USER_PROMPT_SPLIT_MARKER,
    '',
    'LABEL TOOL: Attach text labels to specific elements to identify or explain them.',
    'Use for: Naming parts, calling out features, providing brief explanations.',
    '',
    'HIGHLIGHT TOOL: Make elements glow with emissive color to emphasize important parts.',
    'Colors: WHITE (#FFFFFF) for key features, RED (#FF0000) for warnings, BLUE (#0000FF) for secondary info.',
    '',
    'SHOWCASE TOOL: Create dramatic focus by fading all other elements and keeping target fully visible.',
    'Use for the most important feature reveals.',
    '',
    'ROTATION TOOL: Represents real-life movements like opening doors, hoods, or lids.',
    'Use for mechanical pivoting movement only.',
    '',
    'MOVE TOOL: Translates objects that lift, slide, or shift in real life.',
    'Values are in meters (0.1 = 10cm, 0.5 = 50cm).',
    '',
    'SCALE TOOL: Changes object size, ideal for fill levels or object visibility.',
    'Use y scale for liquid level (0 empty, 0.5 half, 1 full).',
    '',
    'ALPHA TOOL: Sets transparency to see through containers/shells.',
    'Use 0.3 for see-through containers.',
    '',
    '=== JSON FORMAT FOR TOOLS ===',
    'For each stage, you can use any combination of these tools:',
    '',
    '{',
    '  "instruction": "Description of what is taught in this stage",',
    '  "labels": [{"elementName": "component_name", "labelText": "Feature Label"}],',
    '  "colorHighlights": [{"elementName": "component_name", "color": "#FFFFFF"}],',
    '  "showcase": [{"elementName": "component_name"}],',
    '  "rotation": [{"elementName": "component_name", "axis": "Y", "angle": 45}],',
    '  "move": [{"elementName": "component_name", "y": 0.2}],',
    '  "scale": [{"elementName": "component_name", "y": 0.5}],',
    '  "alpha": [{"elementName": "component_name", "alpha": 0.3}]',
    '}',
  ].join('\n')
}

export function buildEnhancementCorePrompt(): string {
  return [
    'You are an INSTRUCTOR teaching the user about a topic. Write educational content that explains concepts directly.',
    '',
    'YOUR JOB: Explain the topic to answer the user question. The 3D visuals move automatically.',
    '',
    'CRITICAL RULES:',
    '- Write educational content that teaches the user.',
    '- NEVER describe visual actions (lifting, moving, highlighting, bringing into view).',
    '- NEVER comment on the presentation itself.',
    '- PLAIN TEXT ONLY (no markdown).',
    '',
    'BAD: "Now we open this cover and highlight the motor."',
    'GOOD: "The cover protects the motor assembly from dust and accidental contact."',
    '',
    'Structure:',
    '- Stage 1: Introduce the topic.',
    '- Middle stages: Explain key concepts with practical detail.',
    '- Final stage: Summarize key takeaways.',
    '',
    'Return valid JSON format only.',
  ].join('\n')
}

export function buildEnhancementExamplesAndRulesPrompt(): string {
  return [
    'EXAMPLE JSON OUTPUT:',
    '[',
    '  {',
    '    "instruction": "This model demonstrates the overall concept and its primary purpose.",',
    '    "labels": [],',
    '    "colorHighlights": [],',
    '    "showcase": [],',
    '    "rotation": [],',
    '    "move": [],',
    '    "scale": [],',
    '    "alpha": []',
    '  },',
    '  {',
    '    "instruction": "This component controls the main process and is critical for performance.",',
    '    "labels": [{"elementName": "Component", "labelText": "Main Controller"}],',
    '    "colorHighlights": [{"elementName": "Component", "color": "#FFFFFF"}],',
    '    "showcase": [],',
    '    "rotation": [],',
    '    "move": [],',
    '    "scale": [],',
    '    "alpha": []',
    '  },',
    '  {',
    '    "instruction": "Key takeaway: understand the core component interactions and safe operating limits.",',
    '    "labels": [],',
    '    "colorHighlights": [],',
    '    "showcase": [],',
    '    "rotation": [],',
    '    "move": [],',
    '    "scale": [],',
    '    "alpha": []',
    '  }',
    ']',
    '',
    'FIRST STAGE RULE:',
    '- The first stage must be an introduction with no visual modifications.',
    '- All tool arrays in first stage should be empty.',
    '',
    'TOOL GUIDELINES:',
    '- Use showcase only for the most important reveal moment.',
    '- Use highlight to draw attention without overusing effects.',
    '- Use at most one label per stage where possible.',
    '- Use exact full ObjectName values from Available Components for elementName.',
    '',
    'TRANSFORMS RESET EACH STAGE:',
    '- Move/rotation/scale do not carry automatically between stages.',
    '- Repeat the same transform in each stage where it should persist.',
    '',
    'Return ONLY a valid JSON array. No extra text.',
  ].join('\n')
}

/**
 * Composes the final system and user prompt pair used for stage-instruction
 * generation, either from a custom prompt or from the default local templates.
 */
export function buildComposedEnhancementPrompts(
  userQuestion: string,
  modelFileName: string,
  serializedElements: any[],
  customPresentationPrompt: string
): { systemPrompt: string; userPrompt: string } {
  const dynamicSection = buildDynamicEnhancementSection(userQuestion, modelFileName, serializedElements)
  const customPrompt = customPresentationPrompt ? customPresentationPrompt.trim() : ''

  if (customPrompt.length > 0) {
    return {
      systemPrompt: dynamicSection + '\n\n' + customPrompt,
      userPrompt: 'Generate the stage instructions as specified above.',
    }
  }

  const corePrompt = buildEnhancementCorePrompt()
  const toolsPrompt = buildToolsPromptSection()
  const examplesAndRules = buildEnhancementExamplesAndRulesPrompt()

  return {
    systemPrompt: dynamicSection + '\n\n' + corePrompt,
    userPrompt: toolsPrompt + '\n\n' + examplesAndRules,
  }
}

/**
 * Splits the server-rendered flavored prompt into system and user segments using
 * the local tool-section marker until the backend returns structured prompt parts.
 */
export function splitRenderedFlavoredPrompt(
  renderedPrompt: string
): { systemPrompt: string; userPrompt: string } | null {
  if (!renderedPrompt) {
    return null
  }

  // Temporary compatibility shim: the render endpoint currently returns one string,
  // so we split it by the tool-section marker until backend returns structured parts.
  const splitIndex = renderedPrompt.indexOf(USER_PROMPT_SPLIT_MARKER)
  if (splitIndex < 0) {
    return null
  }

  const systemPrompt = renderedPrompt.substring(0, splitIndex).trim()
  const userPrompt = renderedPrompt.substring(splitIndex).trim()

  if (!userPrompt) {
    return null
  }

  return {
    systemPrompt: systemPrompt,
    userPrompt: userPrompt,
  }
}

/**
 * Normalizes the text content returned by the different AI proxy response shapes
 * used by the supported providers.
 */
export function extractContentFromAiResponse(data: any): string {
  if (data && Array.isArray(data.choices) && data.choices.length > 0) {
    const firstChoice = data.choices[0]
    if (firstChoice && firstChoice.message && typeof firstChoice.message.content === 'string') {
      return firstChoice.message.content.trim()
    }
  }

  if (data && data.output && Array.isArray(data.output)) {
    const messageEntry = data.output.find(function (item: any) {
      return item && item.type === 'message'
    })

    if (messageEntry && Array.isArray(messageEntry.content)) {
      const textEntry = messageEntry.content.find(function (item: any) {
        return item && (item.type === 'output_text' || item.type === 'text')
      })

      if (textEntry && typeof textEntry.text === 'string') {
        return textEntry.text.trim()
      }
    }
  }

  if (data && data.output && typeof data.output.content === 'string') {
    return data.output.content.trim()
  }

  return ''
}

/**
 * Extracts and validates the stage-instruction JSON array returned by the model.
 * Fence wrappers and leading/trailing prose are stripped before parsing.
 */
export function parseStageInstructions(rawContent: string): StageInstructionWithTools[] {
  if (!rawContent) {
    return []
  }

  let content = rawContent.trim()

  if (content.indexOf('```') === 0) {
    const firstNewline = content.indexOf('\n')
    const lastFence = content.lastIndexOf('```')
    if (firstNewline >= 0 && lastFence > firstNewline) {
      content = content.substring(firstNewline + 1, lastFence).trim()
    }
  }

  const firstBracket = content.indexOf('[')
  const lastBracket = content.lastIndexOf(']')
  if (firstBracket >= 0 && lastBracket > firstBracket) {
    content = content.substring(firstBracket, lastBracket + 1)
  }

  const parsed = JSON.parse(content)
  if (!Array.isArray(parsed)) {
    return []
  }

  const result: StageInstructionWithTools[] = []

  parsed.forEach(function (item: any) {
    if (!item || typeof item.instruction !== 'string') {
      return
    }

    const stageItem: StageInstructionWithTools = {
      instruction: item.instruction,
    }

    if (Array.isArray(item.labels)) {
      stageItem.labels = item.labels
    }
    if (Array.isArray(item.colorHighlights)) {
      stageItem.colorHighlights = item.colorHighlights
    }
    if (Array.isArray(item.showcase)) {
      stageItem.showcase = item.showcase
    }
    if (Array.isArray(item.rotation)) {
      stageItem.rotation = item.rotation
    }
    if (Array.isArray(item.move)) {
      stageItem.move = item.move
    }
    if (Array.isArray(item.scale)) {
      stageItem.scale = item.scale
    }
    if (Array.isArray(item.alpha)) {
      stageItem.alpha = item.alpha
    }
    if (Array.isArray(item.viewpoint)) {
      stageItem.viewpoint = item.viewpoint as ViewpointToolData[]
    }

    result.push(stageItem)
  })

  return result
}

/**
 * Generates stage instructions by optionally rendering a flavored prompt template,
 * calling Spark AI completion, and converting the response into typed stage tools.
 */
export async function generateStageInstructions(
  apiDomain: string,
  tenantId: number,
  jigId: string,
  userQuestion: string,
  modelFileName: string,
  serializedElements: any[],
  customPresentationPrompt: string,
  promptFlavoring?: UseCasePromptFlavoring | null,
  requestConfig: AxiosRequestConfig = {}
): Promise<StageInstructionWithTools[]> {
  if (!apiDomain) {
    return []
  }

  let composedPrompts = buildComposedEnhancementPrompts(
    userQuestion,
    modelFileName,
    serializedElements,
    customPresentationPrompt
  )

  if (!customPresentationPrompt && promptFlavoring && promptFlavoring.templateName) {
    try {
      const renderResponse = await postToSpark(
        apiDomain,
        tenantId,
        AppConst.APIEndpoint.sparkV1Url + '/prompts/' + encodeURIComponent(promptFlavoring.templateName) + '/render',
        { variables: promptFlavoring.renderVariables },
        'json',
        requestConfig
      )
      const renderedPrompt = renderResponse && renderResponse.data && typeof renderResponse.data.rendered === 'string'
        ? renderResponse.data.rendered
        : ''
      const splitPrompts = splitRenderedFlavoredPrompt(renderedPrompt)

      if (splitPrompts) {
        const dynamicSection = buildDynamicEnhancementSection(userQuestion, modelFileName, serializedElements)
        composedPrompts = {
          systemPrompt: splitPrompts.systemPrompt.length > 0
            ? dynamicSection + '\n\n' + splitPrompts.systemPrompt
            : dynamicSection,
          userPrompt: splitPrompts.userPrompt,
        }
      } else {
        consoleWarn(
          'Falling back to default local prompt composition because flavored prompt render response could not be split on "' +
          USER_PROMPT_SPLIT_MARKER +
          '".'
        )
      }
    } catch (renderError) {
      if (isRequestCanceled(renderError)) {
        throw renderError
      }
      consoleWarn(
        'Falling back to default local prompt composition because flavored prompt render failed: ' + String(renderError)
      )
    }
  }

  try {
    const provider = getSparkAiProvider()
    const model = getSparkAiModel()
    const maxTokens = getSparkAiMaxTokens()
    const temperature = getSparkAiTemperature(provider)
    const response = await postToSparkWithFallback(
      apiDomain,
      tenantId,
      'aiComplete',
      {
        provider: provider,
        model: model,
        messages: [
          { role: 'system', content: composedPrompts.systemPrompt },
          { role: 'user', content: composedPrompts.userPrompt },
        ],
        max_completion_tokens: maxTokens,
        max_tokens: maxTokens,
        maxTokens: maxTokens,
        temperature: temperature,
        metadata: {
          operation: 'jig_enhancement',
          jigId: jigId,
          userQuery: userQuestion,
          tags: ['dashboard-migration', 'stage-instructions'],
        },
      },
      'json',
      requestConfig,
    )

    const content = extractContentFromAiResponse(response && response.data ? response.data : {})
    if (!content) {
      return []
    }

    return parseStageInstructions(content)
  } catch (error) {
    if (isRequestCanceled(error)) {
      throw error
    }
    consoleError('Error generating stage instructions:', error)
    return []
  }
}

/**
 * Creates the minimal stage template used when a copied Jig does not already
 * contain a reusable stage structure.
 */
export function createDefaultStage(serializedElements: any[]): any {
  return {
    UID: generateShortUid(),
    StageUid: generateShortUid(),
    StageOrder: 0,
    StageInstructions: '',
    StagePosition: { x: 0, y: 0, z: 0 },
    StageSound: { Uri: '', Delay: 0, Loop: false },
    StageRotation: { x: 0, y: 0, z: 0 },
    StageScale: { x: 1, y: 1, z: 1 },
    Loop: false,
    IsViewpointActiveInStage: false,
    ViewpointDistance: 0,
    ViewpointPosition: { x: 0, y: 0, z: 0 },
    ViewpointRotation: { x: 0, y: 0, z: 0 },
    ViewpointTargetPosition: { x: 0, y: 0, z: 0 },
    SerializedElements: deepClone(serializedElements),
  }
}
